import { InjectionToken, TemplateRef } from '@angular/core';
import { Observable } from 'rxjs';
import { ButtonType } from '../button/button.directive';

// ========================  Types  =============================

export type ChartType = 'bar' | 'pie';
export type ViewType = 'table' | 'chart';
export type ColumnType = 'single' | 'group' | 'button' | 'index';
export type PageChangeDirection = 'forward' | 'backward';
export type apiFilter = Record<string, string[]>;

// ====================== BE Interfaces  ===========================

/** Generic type for extra body parameters to be appended to API requests */
export type ExtraBodyParams = Record<string, unknown>;


export interface TableRequest {
  page: number;
  pageSize?: number;
  sortBy?: Record<string, 'asc' | 'desc'>[];
  filters?: apiFilter;
  returnAll?: boolean;
  search?: string;
  to?: string;
  from?: string;
  getTotalCount?: boolean;
  getAvailableFilters?: boolean;
  /** Generic extra body parameters to be appended to the request body */
  extraBody?: ExtraBodyParams;
  /** When true, filters and getAvailableFilters are not sent to the API */
  skipFilters?: boolean;
}

export interface TableResponse {
  success: boolean; //true
  message: string; //'Customers retrieved successfully';
  messageCode: string; //'S1401013';
  data: ResponseData;
}

export interface ResponseData {
  page: number;
  pages: number;
  pageSize: number;
  rows: Row[];
  schema: Column[];
  filters?: apiFilter;
  totalCount?: number;
}

export type SingleColumn = {
  type: 'single';
  name: string;
  dataType: 'numeric' | 'information' | 'link';
  ignore: boolean;
};

export type NestedColumn = {
  type: 'group';
  name: string;
  groupColumns: SingleColumn[];
  ignore: boolean;
};

export type Column = SingleColumn | NestedColumn;

export type Cell =
  | string
  | boolean
  | string[]
  | {
    [key: string]: string;
  };

export type Row = {
  [key: string]: Cell;
};

export interface GlobalAction {
  actionButton: TemplateRef<any>;
  onSelect: (rows: TableRow[]) => void;
}

export type RowAction = {
  actionButton: TemplateRef<any>;
  headerName: string;
  onClick: (row: TableRow) => void;
};

export type TableButton = {
  name: string;
  icon?: string;
  type: ButtonType;
  color: string;
};

export type CellColoring = {
  colorFn: (value: number) => string;
  columns: string[];
};

export interface CustomCellContext {
  $implicit: Cell;
  value: Cell;
  row: TableRow;
  column: TableHead;
}

export interface CustomTableCell {
  column: string;
  template: TemplateRef<CustomCellContext>;
  minWidth?: number;
  rowHeight?: number;
}


// data:{
// schema:[
//   {
//     type: 'group',
//     name: "groupColumn",
//     groupColumns: [
//       {
//         type: 'single',
//         name: 'name',
//         dataType: 'information',
//         ignore: false,
//       }
//     ],
//     ignore: false
// }
// ]
// rows: [
//   {
//     name: 'test',
//     test3: 10,
//     groupColumn: {
//       name: 'test',
//       test3: 10,
//     },
//   },
// ];
// }


// ======================  FE Interfaces  ===========================

export type Filter = Record<string, FilterOption[]>;

export interface TableData {
  heads: TableHead[];
  rows: TableRow[];
  chartType: ChartType;
}

export interface TableHead {
  name: string;
  type: ColumnType;
  dataType?: 'numeric' | 'information' | 'link';
  asc: boolean; //is sorted ascendingly?
  ignore: boolean; //indicates whether to view the column or not
  groupColumns?: SingleColumn[];
}
export interface TableRow {
  id: string;
  value: Row;
  selected: boolean;
}

export interface FilterOption {
  value: string;
  id: string;
  checked?: boolean;
  selected?: boolean;
}

export type IndexColumn = {
  name: string;
  type: 'index';
  ignore: boolean;
};

export interface TableDataProvider {
  fetchData(params: TableRequest): Observable<TableResponse>;
}

export const TABLE_DATA_PROVIDER = new InjectionToken<TableDataProvider>(
  'TABLE_DATA_PROVIDER'
);
